/**
 * Automatic per-user ("seat") billing (owner-approved design 2026-10-24).
 * Backend: api/subscription (see the backend CLAUDE.md "Seat billing").
 * Only firms that signed up after launch are billed per seat; for every
 * other firm the server answers phase "none" and nothing here shows.
 */

export type SubscriptionPhase =
  | "none"
  | "trial"
  | "trial_ending"
  | "grace"
  | "locked"
  | "active"
  | "payment_failed";

export type SubscriptionStatus = {
  seatBilling: boolean;
  phase: SubscriptionPhase;
  trialEndsAt?: string | null;
  graceEndsAt?: string | null;
  canManage: boolean;
  billingContactName?: string | null;
  seats?: number | null;
};

export type SubscriptionInvoice = {
  id: string;
  number?: string | null;
  date: string;
  total: number;
  amountPaid: number;
  currency: string;
  status: string;
  receiptUrl?: string | null;
  description?: string | null;
};

export type SubscriptionPlan = {
  phase: SubscriptionPhase;
  status: string;
  interval: "month" | "year" | string;
  unitAmount: number;
  currency: string;
  /** Australia / UK: prices include tax. */
  taxInclusive?: boolean;
  seats: number;
  activePeople: number;
  periodTotal: number;
  monthlyUnitAmount?: number | null;
  yearlyUnitAmount?: number | null;
  standardPrice: boolean;
  canSwitchToMonthly: boolean;
  canSwitchToYearly: boolean;
  discountName?: string | null;
  percentOff?: number | null;
  amountOff?: number | null;
  trialEndsAt?: string | null;
  graceEndsAt?: string | null;
  currentPeriodEnd?: string | null;
  nextBillAmount?: number | null;
  nextBillTax?: number | null;
  nextBillDate?: string | null;
  /** Trial without a card: seats x price before tax (Stripe plans no bill yet). */
  nextBillEstimated?: boolean;
  cardBrand?: string | null;
  cardLast4?: string | null;
  billingContactName?: string | null;
  invoices: SubscriptionInvoice[];
};

export type SeatPreview = {
  applies: boolean;
  canManage: boolean;
  billingContactName?: string | null;
  phase: SubscriptionPhase;
  inTrial: boolean;
  trialEndsAt?: string | null;
  seatsNow?: number | null;
  seatsAfter?: number | null;
  interval?: string | null;
  unitAmount?: number | null;
  currency?: string | null;
  taxInclusive?: boolean;
  periodTotalAfter?: number | null;
  proratedAmount?: number | null;
  currentPeriodEnd?: string | null;
  cardBrand?: string | null;
  cardLast4?: string | null;
};

/** The server's 402 body when the firm must add a card (SeatBillingGate). */
export const SUBSCRIPTION_REQUIRED_CODE = "subscription_required";
/** Window event fired when any API call answers subscription_required. */
export const SUBSCRIPTION_REQUIRED_EVENT = "proc:subscription-required";
/** Header telling the server which firm the request is for. */
export const ORG_HEADER = "X-Proc-Org";

export const isSubscriptionRequiredBody = (body: unknown): boolean => {
  if (!body) return false;
  if (typeof body === "string")
    return body.includes(SUBSCRIPTION_REQUIRED_CODE);
  if (typeof body === "object")
    return (body as { code?: unknown }).code === SUBSCRIPTION_REQUIRED_CODE;
  return false;
};

/**
 * Stripe amounts are in cents. Like Xero/Karbon (owner 2026-10-25: "copy what competitors do"): US firms
 * "$49", Canada "$49 CAD", Australia "$49 AUD", UK "£49". Whole amounts drop the cents.
 */
export const formatMoney = (
  cents: number | null | undefined,
  currency = "usd",
  keepCents = false,
): string => {
  const value = (cents ?? 0) / 100;
  const whole = Number.isInteger(value) && !keepCents;
  const code = (currency || "usd").toUpperCase();
  let text: string;
  try {
    text = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: whole ? 0 : 2,
      maximumFractionDigits: whole ? 0 : 2,
    }).format(value);
  } catch {
    text = `$${value.toFixed(whole ? 0 : 2)}`;
  }
  // "$" alone would read as US dollars: name the other dollars.
  return code === "CAD" || code === "AUD" ? `${text} ${code}` : text;
};

/** Tax words for a price: "incl. GST" (Australia) / "incl. VAT" (UK) when tax is included, else "plus tax". */
export const taxNote = (
  taxInclusive: boolean | null | undefined,
  currency?: string | null,
) => {
  if (!taxInclusive) return "plus tax";
  const code = (currency || "").toLowerCase();
  return code === "aud"
    ? "incl. GST"
    : code === "gbp"
    ? "incl. VAT"
    : "incl. tax";
};

/** "Nov 14, 2026" in the viewer's time zone. */
export const formatDay = (iso: string | null | undefined): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/** The local day ("2026-11-14") used to dismiss the purple banner for the day. */
export const localDayKey = (now: Date) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;

export const DISMISS_STORAGE_KEY = "proc_trial_banner_dismissed";

export type BannerKind = "trial_ending" | "grace" | "payment_failed";

export type Banner = {
  kind: BannerKind;
  tone: "purple" | "orange" | "red";
  text: string;
  /** null = no button (people without the billing permission). */
  action: "checkout" | "portal" | null;
  actionLabel?: string;
  dismissible: boolean;
};

const contactName = (status: { billingContactName?: string | null }) =>
  status.billingContactName?.trim() || "your firm's billing contact";

const askContact = (status: { billingContactName?: string | null }) =>
  status.billingContactName?.trim()
    ? `Ask ${status.billingContactName.trim()} (your firm's billing contact)`
    : "Ask your firm's billing contact";

/**
 * Which banner shows on every page (owner's texts, 2026-10-24):
 * - last 3 days of the trial: purple, dismissible for the day;
 * - 4 days after the trial: orange, not dismissible;
 * - a card payment failed: red (billing people only).
 */
export const bannerFor = (
  status: SubscriptionStatus | undefined | null,
  dismissedDay: string | null,
  today: string,
): Banner | null => {
  if (!status?.seatBilling) return null;
  if (status.phase === "trial_ending") {
    if (dismissedDay === today) return null;
    const date = formatDay(status.trialEndsAt);
    return status.canManage
      ? {
          kind: "trial_ending",
          tone: "purple",
          text: `Your free trial ends on ${date}. Add a payment method to keep using ProCharted without interruption.`,
          action: "checkout",
          actionLabel: "Add payment method",
          dismissible: true,
        }
      : {
          kind: "trial_ending",
          tone: "purple",
          text: `Your free trial ends on ${date}. ${askContact(
            status,
          )} to add a payment method.`,
          action: null,
          dismissible: true,
        };
  }
  if (status.phase === "grace") {
    const date = formatDay(status.graceEndsAt);
    return status.canManage
      ? {
          kind: "grace",
          tone: "orange",
          text: `Your free trial has ended. Add a payment method by ${date} to keep access to ProCharted. Your data is safe.`,
          action: "checkout",
          actionLabel: "Add payment method",
          dismissible: false,
        }
      : {
          kind: "grace",
          tone: "orange",
          text: `Your free trial has ended. ${askContact(
            status,
          )} to add a payment method by ${date}.`,
          action: null,
          dismissible: false,
        };
  }
  if (status.phase === "payment_failed" && status.canManage) {
    return {
      kind: "payment_failed",
      tone: "red",
      text: "We couldn't charge your card for ProCharted. Update your payment method to keep your plan active.",
      action: "portal",
      actionLabel: "Update payment method",
      dismissible: false,
    };
  }
  return null;
};

export const intervalWord = (interval: string | null | undefined) =>
  interval === "year" ? "year" : "month";

/** "$49 per user / month" or "$39 per user / month, billed yearly ($468)". */
export const priceLine = (
  unitAmount: number,
  interval: string,
  currency = "usd",
) =>
  interval === "year"
    ? `${formatMoney(
        Math.round(unitAmount / 12),
        currency,
      )} per user / month, billed yearly (${formatMoney(
        unitAmount,
        currency,
      )} per user / year)`
    : `${formatMoney(unitAmount, currency)} per user / month`;

/** Yearly saving per user per year, from the two standard prices (e.g. $120). */
export const yearlySaving = (
  monthly: number | null | undefined,
  yearly: number | null | undefined,
): number | null => {
  if (!monthly || !yearly) return null;
  const saving = monthly * 12 - yearly;
  return saving > 0 ? saving : null;
};

export const brandName = (brand: string | null | undefined) => {
  const b = (brand || "").toLowerCase();
  const names: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    amex: "American Express",
    discover: "Discover",
    diners: "Diners Club",
    jcb: "JCB",
    unionpay: "UnionPay",
  };
  return names[b] || (b ? b.charAt(0).toUpperCase() + b.slice(1) : "Card");
};

export const cardLine = (
  brand: string | null | undefined,
  last4: string | null | undefined,
) => (last4 ? `${brandName(brand)} •••• ${last4}` : "");

/** Status pill on Plan & seats. */
export const phasePill = (
  phase: SubscriptionPhase,
): { label: string; tone: "purple" | "green" | "orange" | "red" | "gray" } => {
  switch (phase) {
    case "trial":
      return { label: "Free trial", tone: "purple" };
    case "trial_ending":
      return { label: "Trial ending soon", tone: "purple" };
    case "grace":
      return { label: "Trial ended", tone: "orange" };
    case "locked":
      return { label: "Payment method needed", tone: "red" };
    case "payment_failed":
      return { label: "Payment failed", tone: "red" };
    case "active":
      return { label: "Active", tone: "green" };
    default:
      return { label: "—", tone: "gray" };
  }
};

export const invoiceStatusLabel = (status: string) => {
  switch ((status || "").toLowerCase()) {
    case "paid":
      return "Paid";
    case "open":
      return "Due";
    case "uncollectible":
      return "Unpaid";
    case "void":
      return "Cancelled";
    default:
      return status || "";
  }
};

export type SeatConfirmCopy = {
  title: string;
  lines: { label: string; value: string }[];
  note: string;
  confirmLabel: string;
};

/**
 * The Add user / invite confirmation (owner-approved mockup). With the billing
 * permission: plan, seats N -> N+1, new total, what is charged and the card.
 * Without: no prices at all.
 */
export const seatConfirmCopy = (
  preview: SeatPreview,
  name: string,
  direct = false,
): SeatConfirmCopy => {
  const who = name.trim() || "this person";
  const first = who.split(" ")[0];
  const when = direct ? "today" : `when ${first} accepts`;
  if (!preview.canManage) {
    return {
      title: direct ? `Add ${who}?` : `Invite ${who}?`,
      lines: [],
      note: direct
        ? "A paid seat is added to your firm's plan. Your firm's billing contact gets the receipt."
        : `When ${who} accepts, a paid seat is added to your firm's plan. Your firm's billing contact gets the receipt.`,
      confirmLabel: direct ? "Add user" : "Send invite",
    };
  }
  const currency = preview.currency || "usd";
  const interval = intervalWord(preview.interval);
  const lines: { label: string; value: string }[] = [];
  if (preview.unitAmount != null)
    lines.push({
      label: "Plan",
      value: priceLine(preview.unitAmount, interval, currency),
    });
  if (preview.seatsNow != null && preview.seatsAfter != null)
    lines.push({
      label: "Seats",
      value: `${preview.seatsNow} → ${preview.seatsAfter}`,
    });
  if (preview.periodTotalAfter != null)
    lines.push({
      label: interval === "year" ? "New yearly total" : "New monthly total",
      value: `${formatMoney(preview.periodTotalAfter, currency)} / ${interval}`,
    });
  const card = cardLine(preview.cardBrand, preview.cardLast4);
  if (preview.inTrial) {
    lines.push({
      label: "Charged now",
      value: `Nothing — free until your trial ends${
        preview.trialEndsAt ? ` on ${formatDay(preview.trialEndsAt)}` : ""
      }`,
    });
  } else if (preview.proratedAmount != null) {
    lines.push({
      label: direct ? "Charged today" : `Charged ${when}`,
      value: `About ${formatMoney(
        preview.proratedAmount,
        currency,
        true,
      )} for the days left in this billing period${
        preview.currentPeriodEnd
          ? ` (until ${formatDay(preview.currentPeriodEnd)})`
          : ""
      }${card ? `, to ${card}` : ""}, ${
        preview.taxInclusive ? taxNote(true, currency) : "plus any tax"
      }`,
    });
  }
  return {
    title: `Add a seat for ${who}?`,
    lines,
    note: "The receipt is emailed to your firm's billing contact.",
    confirmLabel: direct ? "Add seat & add user" : "Add seat & send invite",
  };
};

export const billingContactLabel = contactName;

/** Plan & seats "Next bill" line. */
export const nextBillLine = (plan: {
  nextBillAmount?: number | null;
  nextBillTax?: number | null;
  nextBillDate?: string | null;
  nextBillEstimated?: boolean;
  currency: string;
  taxInclusive?: boolean;
  phase: SubscriptionPhase;
}): string => {
  if (plan.nextBillAmount != null && plan.nextBillDate) {
    if (plan.nextBillEstimated)
      return `About ${formatMoney(
        plan.nextBillAmount,
        plan.currency,
        true,
      )} ${taxNote(plan.taxInclusive, plan.currency)} on ${formatDay(
        plan.nextBillDate,
      )}, once a card is added`;
    return `${formatMoney(
      plan.nextBillAmount,
      plan.currency,
      true,
    )} on ${formatDay(plan.nextBillDate)}${
      plan.nextBillTax
        ? ` (incl. ${formatMoney(plan.nextBillTax, plan.currency, true)} tax)`
        : plan.taxInclusive
        ? ` (${taxNote(true, plan.currency)})`
        : ""
    }`;
  }
  return plan.phase === "grace" || plan.phase === "locked"
    ? "After a payment method is added"
    : "—";
};

/** Free seasonal users (owner 2026-10-25): backend GET api/subscription/seasonal. */
export type SeasonalInfo = {
  seatBilling: boolean;
  /** Feb 1 – Apr 30 now. */
  inSeason: boolean;
  /** May 1 00:00 UTC: free until the day before. */
  seasonEndsAt: string;
  nextSeasonStartsAt: string;
  /** Free spots = the firm's paid seats. */
  allowed: number;
  /** Seasonal people + seasonal invitations not yet accepted. */
  used: number;
  userIds: string[];
  invitationIds: string[];
};

/** "Apr 30, 2027": the last free day (the season ends May 1 00:00 UTC). */
export const seasonLastDay = (endsAt: string | null | undefined): string => {
  const end = endsAt ? new Date(endsAt).getTime() : NaN;
  if (Number.isNaN(end)) return "Apr 30";
  return new Date(end - 1).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

export const seasonalSpotsLine = (info: SeasonalInfo) =>
  `${info.used} of ${info.allowed} free seasonal spot${
    info.allowed === 1 ? "" : "s"
  } used`;

const SEASON_RULE = "one per paid user";

/** The invite window's "Seasonal user" switch: shown only in season for firms billed per user. */
export const seasonalInviteState = (info: SeasonalInfo | null | undefined) => {
  if (!info?.seatBilling || !info.inSeason)
    return { show: false, disabled: true, note: "" };
  const until = seasonLastDay(info.seasonEndsAt);
  if (info.used >= info.allowed)
    return {
      show: true,
      disabled: true,
      note: `All ${info.allowed} free seasonal spot${
        info.allowed === 1 ? " is" : "s are"
      } used (${SEASON_RULE}).`,
    };
  return {
    show: true,
    disabled: false,
    note: `Free until ${until}, then switched off automatically (nothing is deleted). ${seasonalSpotsLine(
      info,
    )}.`,
  };
};

/**
 * Edit user: make an existing person seasonal or paid again. Making a paid person seasonal also lowers
 * the paid seats (= the spots) by one, so it needs used + 1 <= allowed - 1 (same rule as the server).
 */
export const seasonalEditState = (
  info: SeasonalInfo | null | undefined,
  userId: string,
  isBillingContact: boolean,
) => {
  const isSeasonal = !!info?.userIds.includes(userId);
  if (!info?.seatBilling || (!info.inSeason && !isSeasonal))
    return { show: false, isSeasonal, disabled: true, note: "" };
  const until = seasonLastDay(info.seasonEndsAt);
  if (isSeasonal)
    return {
      show: true,
      isSeasonal,
      disabled: false,
      note: `Free until ${until}, then switched off automatically. Turning this off makes them a paid user now.`,
    };
  if (isBillingContact)
    return {
      show: true,
      isSeasonal,
      disabled: true,
      note: "The firm's billing contact is always a paid user.",
    };
  if (info.used + 1 > info.allowed - 1)
    return {
      show: true,
      isSeasonal,
      disabled: true,
      note: `No free seasonal spot left (${SEASON_RULE}; ${seasonalSpotsLine(
        info,
      )}).`,
    };
  return {
    show: true,
    isSeasonal,
    disabled: false,
    note: `Free until ${until}: one paid seat less from your next bill (no refund for this period). ${seasonalSpotsLine(
      info,
    )}.`,
  };
};

/** Plan & seats: "+ 2 seasonal users, free until Apr 30, 2027 (1 spot left)"; "" when nothing to say. */
export const seasonalPlanLine = (info: SeasonalInfo | null | undefined) => {
  if (!info?.seatBilling) return "";
  const people = info.userIds.length;
  const left = Math.max(0, info.allowed - info.used);
  // Out of season nobody is seasonal any more (archived on May 1).
  if (!info.inSeason) return "";
  const spots = `${left} free seasonal spot${
    left === 1 ? "" : "s"
  } left until ${seasonLastDay(info.seasonEndsAt)}`;
  return people
    ? `+ ${people} seasonal user${people === 1 ? "" : "s"} (free) · ${spots}`
    : spots;
};
